import React, { useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RegulationsHelpModal from './RegulationsHelpModal';

/**
 * Shared layout component with role-aware navigation, user info, and logout.
 *
 * - admin: sees link to Admin Portal only
 * - event: sees link to Event Management only
 * - user: sees Race Events, Crew, Documents
 *
 * Requirement 1.5 — On logout, terminate session and clear tokens.
 */
const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showHelpModal, setShowHelpModal] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const role = user?.role ?? 'user';

  return (
    <div className="app-layout">
      <header className="app-header">
        <nav>
          {role === 'admin' && (
            <Link to="/admin">Admin Portal</Link>
          )}
          {role === 'event' && (
            <Link to="/events">Event Management</Link>
          )}
          {role === 'user' && (
            <>
              <Link to="/">Race Events</Link>
              <Link to="/race-events">Build Crew List</Link>
              <Link to="/crew">Crew</Link>
              <Link to="/boat-info">Boat Info</Link>
              <Link to="/documents">Documents</Link>
            </>
          )}
        </nav>
        {user && (
          <div className="app-header-user">
            <button
              className="help-button"
              onClick={() => setShowHelpModal(true)}
              aria-label="Ask about rules and regulations"
              title="Ask about rules and regulations"
            >
              ?
            </button>
            <span style={{ color: '#ccc', fontSize: '0.875rem', marginRight: '0.5rem' }}>
              {user.username}
            </span>
            <span style={{ color: '#8bb8e0', fontSize: '0.75rem', marginRight: '1rem', textTransform: 'capitalize' }}>
              ({role})
            </span>
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        )}
      </header>
      <main className="app-main">
        <Outlet />
      </main>
      <RegulationsHelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />
    </div>
  );
};

export default Layout;
